import {
  AddRecurringAllowanceApiPayload,
  AddRecurringAllowancePayload,
  AddRecurringAllowanceResponse,
  DeleteRecurringAllowancePayload,
  DeleteRecurringAllowanceResponse,
  GetRecurringAllowancesApiResponse,
  GetRecurringAllowancesPayload,
  GetRecurringAllowancesResponse,
  UpdateRecurringAllowanceApiPayload,
  UpdateRecurringAllowancePayload,
  UpdateRecurringAllowanceResponse
} from './apiResponseTypes'
import { getRequest } from 'Services/baseRequests/getRequest'
import { postRequest } from 'Services/baseRequests/postRequest'
import { putRequest } from 'Services/baseRequests/putRequest'
import { deleteRequest } from 'Services/baseRequests/deleteRequest'
import { CurrencyCode } from 'Utils/types/currencies'

export const getRecurringAllowances = async ({
  perPage,
  pageNumber,
  benefitProgramId,
  userId
}: GetRecurringAllowancesPayload): Promise<GetRecurringAllowancesResponse> => {
  const params = new URLSearchParams()

  if (benefitProgramId) params.append('benefit_program_id', benefitProgramId.toString())
  if (userId) params.append('user_id', userId.toString())
  params.append('per_page', perPage.toString())
  params.append('page', pageNumber.toString())

  const response = await getRequest<GetRecurringAllowancesApiResponse>(
    `/admin/recurring_allowances?${params.toString()}`
  )

  return {
    statusCode: response.statusCode,
    success: response.success ?? false,
    message: response.message ?? '',
    data:
      response.success && response.data
        ? {
            recurringAllowances: response.data.recurring_allowances.map((allowance) => ({
              id: allowance.id,
              userId: allowance.user_id,
              benefitProgramId: allowance.benefit_program_id,
              allowanceType: allowance.allowance_type,
              startDate: allowance.start_date,
              amount: parseFloat(allowance.amount),
              cadence: allowance.cadence,
              duration: allowance.duration,
              active: allowance.active,
              notes: allowance.notes,
              currencyCode: allowance.currency_code as CurrencyCode,
              nextOccurrence: allowance.next_occurrence,
              user: allowance.user && {
                id: allowance.user.id,
                firstName: allowance.user.first_name,
                lastName: allowance.user.last_name,
                email: allowance.user.email
              }
            })),
            totalRecurringAllowances: response.data.total_records
          }
        : undefined
  }
}

export const addRecurringAllowance = async ({
  userId,
  benefitProgramId,
  allowanceType,
  startDate,
  amount,
  cadence,
  duration,
  active,
  notes
}: AddRecurringAllowancePayload): Promise<AddRecurringAllowanceResponse> => {
  const payload: AddRecurringAllowanceApiPayload = {
    user_id: userId.toString(),
    benefit_program_id: benefitProgramId.toString(),
    allowance_type: allowanceType,
    start_date: startDate,
    amount
  }

  // Add optional fields if provided
  if (cadence !== undefined) payload.cadence = cadence
  if (duration !== undefined) payload.duration = duration
  if (active !== undefined) payload.active = active
  if (notes !== undefined) payload.notes = notes

  const response = await postRequest<
    AddRecurringAllowanceResponse,
    AddRecurringAllowanceApiPayload
  >(`/admin/recurring_allowances`, payload)

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export const updateRecurringAllowance = async ({
  id,
  allowanceType,
  startDate,
  amount,
  cadence,
  duration,
  active,
  notes
}: UpdateRecurringAllowancePayload): Promise<UpdateRecurringAllowanceResponse> => {
  const payload: UpdateRecurringAllowanceApiPayload = {}

  // Add only provided fields to payload
  if (allowanceType !== undefined) payload.allowance_type = allowanceType
  if (startDate !== undefined) payload.start_date = startDate
  if (amount !== undefined) payload.amount = amount
  if (cadence !== undefined) payload.cadence = cadence
  if (duration !== undefined) payload.duration = duration
  if (active !== undefined) payload.active = active
  if (notes !== undefined) payload.notes = notes

  const response = await putRequest<
    UpdateRecurringAllowanceResponse,
    UpdateRecurringAllowanceApiPayload
  >(`/admin/recurring_allowances/${id}`, payload)

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export const deleteRecurringAllowance = async ({
  id
}: DeleteRecurringAllowancePayload): Promise<DeleteRecurringAllowanceResponse> => {
  const response = await deleteRequest<DeleteRecurringAllowanceResponse>(
    `/admin/recurring_allowances/${id}`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}
